import React from 'react';
import { Box, Container, Typography, Link, Stack, IconButton, useMediaQuery, useTheme } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const Footer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const handleNavigation = (path: string) => {
    navigate(path);
  };
  
  return (
  <Box component="footer" sx={{ bgcolor: 'background.paper', py: 4, mt: 8, borderTop: 1, borderColor: 'divider' }} data-hook="footer-root">
    <Container maxWidth="lg">
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4} justifyContent="space-between" alignItems="center">
        {/* Brand/Logo */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: { xs: 2, sm: 0 },
          justifyContent: { xs: 'center', sm: 'flex-start' }
        }}>
          <img 
            src="/logo.png" 
            alt="Fushflow Logo" 
            style={{ 
              height: isMobile ? 28 : 36, 
              marginRight: isMobile ? 8 : 12 
            }} 
          />
          <Typography 
            variant={isMobile ? "h6" : "h6"} 
            sx={{ 
              fontWeight: 'bold',
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}
          >
            Fushflow
          </Typography>
        </Box>
        {/* Navigation Links */}
        <Stack 
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1, sm: isMobile ? 2 : 4 }}
          sx={{
            alignItems: 'center',
            textAlign: { xs: 'center', sm: 'left' },
            gap: i18n.language === 'he' ? '24px' : { xs: '8px', sm: '16px' },
            '& .MuiLink-root': {
              padding: { xs: '2px 4px', sm: '4px 8px' },
              borderRadius: '4px',
              transition: 'all 0.2s ease',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              }
            }
          }}
        >
          <Link onClick={() => handleNavigation('/')} color="inherit" underline="hover">{t('public-layout.nav.home')}</Link>
          <Link onClick={() => handleNavigation('/about')} color="inherit" underline="hover">{t('public-layout.nav.about')}</Link>
          <Link onClick={() => handleNavigation('/contact')} color="inherit" underline="hover">{t('public-layout.nav.contact')}</Link>
          <Link onClick={() => handleNavigation('/privacy-policy')} color="inherit" underline="hover">{t('privacy-policy')}</Link>
          <Link onClick={() => handleNavigation('/terms-of-service')} color="inherit" underline="hover">{t('terms-of-service')}</Link>
        </Stack>
        {/* Contact Info - Social links removed (AC #7) */}
        <Stack 
          direction="row" 
          spacing={{ xs: 1, sm: 2 }} 
          alignItems="center"
          sx={{ justifyContent: { xs: 'center', sm: 'flex-end' } }}
        >
          <IconButton 
            href="https://wa.me/972547833192" 
            target="_blank" 
            rel="noopener" 
            color="success"
            size={isMobile ? "small" : "medium"}
          >
            <WhatsAppIcon fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
        </Stack>
      </Stack>
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        spacing={2} 
        justifyContent="space-between" 
        alignItems="center" 
        sx={{ mt: 3 }}
      >
        <Typography 
          variant="body2" 
          color="text.primary"
          sx={{ 
            textAlign: { xs: 'center', sm: 'left' },
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 0.5, sm: 0 }
          }}
        >
          <Box component="span">
            <strong>Email:</strong> <Link href="mailto:info@fushflow.com" color="inherit" underline="hover">info@fushflow.com</Link>
          </Box>
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>&nbsp;|&nbsp;</Box>
          <Box component="span">
            <strong>WhatsApp:</strong> <Link href="https://wa.me/972547833192" color="inherit" underline="hover" target="_blank" rel="noopener">054-783-3192</Link>
          </Box>
        </Typography>
        <Typography 
          variant="body2" 
          color="text.primary" 
          sx={{ 
            mt: { xs: 1, sm: 0 },
            textAlign: { xs: 'center', sm: 'right' },
            fontSize: { xs: '0.75rem', sm: '0.875rem' }
          }}
        >
          © {new Date().getFullYear()} Fushflow. All rights reserved.
        </Typography>
      </Stack>
    </Container>
  </Box>
  );
};

export default Footer; 